/**
 * Flatten the document list table's rows into the single ordered array the
 * virtualizer renders.
 *
 * Two inputs decide the order: either the parent hands down an explicit row
 * list (search results and browse-mode tag filtering, where folders interleave
 * with document hits), or we render the current folder's own contents —
 * folders, then container files, then documents. Either way a document whose
 * version group is expanded is followed by its older versions and a "view full
 * history" link — the version badge means the same thing in every mode, so it
 * must expand in every mode. Search results only ever contain the latest
 * version of a lineage (`is_latest_version = true`), so an expansion can never
 * duplicate a row that search already surfaced.
 *
 * EVERY row the table paints comes from here. Folder and container rows used to
 * be rendered as plain blocks above the virtualizer, inside the same scroll
 * container: the virtualizer then computed its visible window from a scroll
 * offset that did not account for their height, so once those blocks were
 * taller than the viewport the window never covered the rows actually on screen
 * and the document area rendered blank. Keep new row types in this list rather
 * than adding a sibling block.
 *
 * Kept pure and structurally typed so the ordering can be tested without
 * mounting the 3k-line table.
 */

/** The only document fields the flattener reads. */
export interface FlattenableDoc {
  id: string;
  filename: string;
  version_group: string | null;
}

/**
 * A confirmed version of a versioned folder — today only the project's Brief
 * folder, whose versions are the brief's. Opening one leaves the list.
 */
export interface FolderVersionRow {
  id: number;
  versionNo: number;
  createdAt: string;
  confirmedByName: string | null;
  note: string | null;
}

/**
 * One rendered row: a document, an expanded group's history link, a folder, a
 * container file (a navigable `.zip` / a sealed signed container), or one of an
 * expanded versioned folder's versions.
 *
 * A container is its own kind rather than a flagged `"doc"`: it renders a
 * different component, takes none of the tag/breadcrumb/version props, and must
 * never gain version expansion.
 */
export type FlattenedRow<TDoc, TFolder, TSeg> =
  | {
      kind: "doc";
      doc: TDoc;
      isExpandedVersion: boolean;
      anchorId: string;
      breadcrumbSegments?: TSeg[] | null;
    }
  | { kind: "history-link"; versionGroup: string; filename: string; anchorId: string }
  | { kind: "folder"; folder: TFolder; anchorId: string }
  | { kind: "container"; doc: TDoc; anchorId: string }
  | { kind: "folder-version"; folderId: string; version: FolderVersionRow; anchorId: string };

/** A row in the parent-supplied ordered list (search / tag-filter mode). */
export type OrderedRow<TDoc, TFolder, TSeg> =
  | { kind: "folder"; folder: TFolder }
  | { kind: "document"; doc: TDoc; breadcrumbSegments: TSeg[] | null };

export interface FlattenDocumentRowsInput<TDoc, TFolder, TSeg> {
  /** Explicit row order from the parent; `undefined` means normal browsing. */
  orderedRows: readonly OrderedRow<TDoc, TFolder, TSeg>[] | undefined;
  /** Documents of the current folder, used when `orderedRows` is undefined. */
  documents: readonly TDoc[];
  /**
   * Subfolders of the current folder. Browse mode only — in search mode the
   * folders that matched are already positioned inside `orderedRows`.
   */
  folders?: readonly TFolder[];
  /**
   * Container files of the current folder, rendered as navigable folder-like
   * rows between the folders and the documents. Browse mode only, for the same
   * reason as `folders`.
   */
  containerDocs?: readonly TDoc[];
  /** Version groups the user has expanded. */
  expandedVersionGroups: ReadonlySet<string>;
  /** Older versions fetched per version group, newest first, anchor excluded. */
  versionDocsCache: ReadonlyMap<string, { docs: TDoc[] }>;
  /**
   * A versioned folder whose version list is open: its versions (newest first)
   * follow its row, wherever that row sits. Absent or null when none is open.
   */
  expandedFolderVersions?: { folderId: string; versions: readonly FolderVersionRow[] } | null;
}

export function flattenDocumentRows<
  TDoc extends FlattenableDoc,
  TFolder extends { id: string },
  TSeg,
>({
  orderedRows,
  documents,
  folders,
  containerDocs,
  expandedVersionGroups,
  versionDocsCache,
  expandedFolderVersions,
}: FlattenDocumentRowsInput<TDoc, TFolder, TSeg>): FlattenedRow<TDoc, TFolder, TSeg>[] {
  const out: FlattenedRow<TDoc, TFolder, TSeg>[] = [];

  /** Append a folder row, and its versions when its version list is open. */
  const pushFolder = (folder: TFolder) => {
    out.push({ kind: "folder", folder, anchorId: folder.id });
    if (expandedFolderVersions?.folderId !== folder.id) return;
    for (const version of expandedFolderVersions.versions) {
      out.push({ kind: "folder-version", folderId: folder.id, version, anchorId: folder.id });
    }
  };

  /** Append an anchor document's expanded older versions and history link. */
  const pushExpandedVersions = (doc: TDoc) => {
    const vg = doc.version_group;
    if (!vg || !expandedVersionGroups.has(vg)) return;
    const versions = versionDocsCache.get(vg)?.docs ?? [];
    for (const version of versions) {
      out.push({ kind: "doc", doc: version, isExpandedVersion: true, anchorId: doc.id });
    }
    // No older versions means nothing to link to — the modal would open empty.
    if (versions.length > 0) {
      out.push({
        kind: "history-link",
        versionGroup: vg,
        filename: doc.filename,
        anchorId: doc.id,
      });
    }
  };

  if (orderedRows) {
    for (const row of orderedRows) {
      if (row.kind === "folder") {
        pushFolder(row.folder);
        continue;
      }
      out.push({
        kind: "doc",
        doc: row.doc,
        isExpandedVersion: false,
        anchorId: row.doc.id,
        breadcrumbSegments: row.breadcrumbSegments,
      });
      pushExpandedVersions(row.doc);
    }
    return out;
  }

  for (const folder of folders ?? []) pushFolder(folder);
  // No `pushExpandedVersions`: a container row carries no version badge, so it
  // has nothing to expand even when its lineage is expanded elsewhere.
  for (const doc of containerDocs ?? []) {
    out.push({ kind: "container", doc, anchorId: doc.id });
  }
  for (const doc of documents) {
    out.push({ kind: "doc", doc, isExpandedVersion: false, anchorId: doc.id });
    pushExpandedVersions(doc);
  }
  return out;
}

/**
 * A stable identity for a rendered row, for the virtualizer's `getItemKey`.
 *
 * Without one the measurement cache is keyed by INDEX: the folder query
 * resolves after the documents do, every index shifts by the number of folders,
 * and each row is then laid out at a height measured for a different row.
 * Prefixed by kind because a container and a document row can name the same
 * document id, and an expanded version row carries its own document's id.
 */
export function flattenedRowKey<TDoc extends { id: string }, TFolder extends { id: string }, TSeg>(
  row: FlattenedRow<TDoc, TFolder, TSeg>
): string {
  switch (row.kind) {
    case "folder":
      return `folder:${row.folder.id}`;
    case "container":
      return `container:${row.doc.id}`;
    case "history-link":
      return `history:${row.versionGroup}`;
    case "doc":
      return `doc:${row.doc.id}`;
    case "folder-version":
      return `folder-version:${row.folderId}:${row.version.id}`;
  }
}

/** A row the user can check: the id plus which selection store owns it. */
export type SelectableItem = { type: "folder" | "document"; id: string };

/**
 * The selectable rows of a flattened list, in render order — the list
 * shift-click ranges are resolved against.
 *
 * Derived from the very rows the virtualizer paints rather than rebuilt from
 * the props, so selection order cannot drift from render order: that drift is
 * what left container files out of every range while they were rendered in a
 * block of their own.
 *
 * Expanded older versions, history links and folder versions are skipped: none
 * is a selectable item, and all take virtualizer slots. Index in this list is
 * therefore NOT the virtualizer index — callers look themselves up by id.
 */
export function selectableItemsFromRows<
  TDoc extends { id: string },
  TFolder extends { id: string },
  TSeg,
>(rows: readonly FlattenedRow<TDoc, TFolder, TSeg>[]): SelectableItem[] {
  const items: SelectableItem[] = [];
  for (const row of rows) {
    if (row.kind === "folder") items.push({ type: "folder", id: row.folder.id });
    else if (row.kind === "container") items.push({ type: "document", id: row.doc.id });
    else if (row.kind === "doc" && !row.isExpandedVersion) {
      items.push({ type: "document", id: row.doc.id });
    }
  }
  return items;
}
